import type { useNavigate } from "@tanstack/react-router";
import { orpcClient } from "@/lib/auth-client";
import { getGatewayInternalRedirect } from "@/lib/redirect-utils";
import { hasPendingInviteToken } from "@/lib/pending-pricing-invite";
import type { RuntimeConfig } from "@/lib/runtime-config";

type NavigateFn = ReturnType<typeof useNavigate>;

/**
 * Send a fully-authenticated user to their final post-auth destination, with the
 * AUTH-154 duplicate-account check applied inline.
 *
 * Call this from EVERY place that finishes authentication and lands the user in
 * the app — TOTP/OTP (`/2fa`), step-up, passkey login, and security-page
 * verification. Centralizing the redirect here is the whole point: a flagged
 * duplicate can no longer slip into the app because one of those pages forgot to
 * check before doing `window.location.href`.
 *
 * `target` is the already-resolved destination (each caller owns its own
 * `safePostAuthRedirect` / fallback policy — e.g. the security page falls back
 * to the client app URL, others to `/`).
 *
 * Resolution order:
 *  1. Pending invitation → hand to the gateway index `/` for explicit confirmation
 *     (the capability remains only in session storage).
 *  2. Internal gateway route (e.g. `/security`) → SPA navigate; not an app entry.
 *  3. External app destination → check duplicate; if flagged, show the warning;
 *     otherwise leave the gateway via `window.location.href`.
 *
 * `skipDuplicateCheck`: gate screens (/2fa, /step-up, /enroll-mfa) show the
 * duplicate warning as a splash modal BEFORE the MFA step. A caller that
 * already resolved the check (not flagged, or the user acknowledged the
 * modal) passes true so the user isn't warned a second time on the way out.
 * Callers that couldn't check (or have no gate screen, e.g. one-step passkey
 * login) leave it false and this safety net still applies.
 */
export async function navigateToFinalDestination({
  target,
  navigate,
  runtimeConfig,
  skipDuplicateCheck = false,
}: {
  target: string;
  navigate: NavigateFn;
  runtimeConfig: RuntimeConfig;
  skipDuplicateCheck?: boolean;
}): Promise<void> {
  if (hasPendingInviteToken()) {
    navigate({
      to: "/",
      search: { redirect_uri: target },
    });
    return;
  }

  const internalTarget = getGatewayInternalRedirect(target, runtimeConfig);
  if (internalTarget) {
    navigate({ to: internalTarget });
    return;
  }

  const duplicate = skipDuplicateCheck
    ? null
    : await orpcClient.auth.checkDuplicate().catch(() => null);
  if (duplicate?.isDuplicate) {
    navigate({
      to: "/duplicate-account",
      search: {
        redirect_uri: target,
        duplicateOfEmail: duplicate.duplicateOfEmail ?? undefined,
      },
    });
    return;
  }

  window.location.href = target;
}
